/**
 * IMBEE-8954 (REQ-CWC-02) — "Origin-checked campaign message ingestion".
 *
 * design.md "The origin check is new, not a refactor": the widget's existing `EXTERNAL_MESSAGE`
 * listener stays origin-unchecked (pre-existing behaviour, unrelated to this proposal). This
 * check applies ONLY to the three new campaign message types, and only accepts a message whose
 * `event.origin` matches the origin of the `parent` URL the widget was booted with — the loader's
 * own origin, not any other embedder.
 *
 * `parentUrl` is the FULL url (`document.location.href` of the host page, per `App.tsx`'s
 * `setParentUrl(params.get('parent'))`), so this compares by origin, not string equality.
 */
export const isTrustedCampaignOrigin = (eventOrigin: string, parentUrl: string): boolean => {
  if (!eventOrigin) return false;
  return campaignParentOrigin(parentUrl) === eventOrigin;
};

/**
 * The loader's ORIGIN, or `null` when `parent` is absent or is not a URL.
 *
 * `parent` is a query-string value on a URL the host page controls, so it can be anything at all —
 * and `postMessage`'s second argument is not a string it tolerates being wrong: a target origin it
 * cannot parse throws a `SyntaxError` synchronously. Thrown from a React effect that runs on the
 * app's first commit, that took the WHOLE widget down (`#root` left with no children) with no error
 * boundary anywhere above it.
 *
 * `null` is the same answer as "no parent" and gets the same treatment — announce to nobody — which
 * is already a supported state: the CMS preview and a direct visit both boot without a parent.
 *
 * The origin, never the full URL: it is the only part the trust decision is made on, and passing a
 * whole URL where an origin is expected only works because the browser throws the rest away.
 *
 * `http(s)` only. A `javascript:` or `data:` URL PARSES — `new URL('javascript:alert(1)')` does not
 * throw — and yields the opaque origin `'null'`, a truthy string that would be posted to as if it
 * were a real target and could never match any `event.origin` worth trusting. A loader always runs
 * on a page, so anything else is a value nobody meant.
 */
export const campaignParentOrigin = (parentUrl: string | null | undefined): string | null => {
  if (!parentUrl) return null;
  try {
    const { origin, protocol } = new URL(parentUrl);
    return protocol === 'http:' || protocol === 'https:' ? origin : null;
  } catch {
    return null;
  }
};

export const CAMPAIGN_MESSAGE_TYPES = ['IMBEE_PAGE_CONTEXT', 'IMBEE_EXIT_INTENT'] as const;

export type CampaignMessageType = (typeof CAMPAIGN_MESSAGE_TYPES)[number];

export const isCampaignMessageType = (type: unknown): type is CampaignMessageType =>
  typeof type === 'string' && (CAMPAIGN_MESSAGE_TYPES as readonly string[]).includes(type);
